import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useGetCarQuery, useUpdateCarMutation } from 'services/api/cars.api';
import { Car, CarTypes, FuelTypes, Transmissions } from 'services/types/car';

/**
 * EditCar component for editing car details
 *
 * @returns JSX.Element - The EditCar component
 */
const EditCar: React.FC = () => {
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Partial<Car>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: car, isLoading: isLoadingCar } = useGetCarQuery(parseInt(carId || '0', 10));
  const [updateCar] = useUpdateCarMutation();

  useEffect(() => {
    if (car) {
      setFormData({
        referenceId: car.referenceId || '',
        brand: car.brand || '',
        model: car.model || '',
        version: car.version || '',
        year: car.year || undefined,
        km: car.km || undefined,
        price: car.price || undefined,
        secondaryPrice: car.secondaryPrice || null,
        image: car.image || '',
        transmission: car.transmission || undefined,
        carType: car.carType || undefined,
        fuelType: car.fuelType || undefined,
        branchOfficeDirection: car.branchOfficeDirection || '',
        color: car.color || '',
        link: car.link || '',
        monthlyFee: car.monthlyFee || null,
        licensePlate: car.licensePlate || '',
        originalImageUrl: car.originalImageUrl || '',
        promotions: car.promotions || '',
      });
    }
  }, [car]);

  const handleFieldChange = (field: keyof Car, value: string | number | boolean | null | undefined) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const navigateToCars = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/cars');
    }
  };

  const handleSubmit = async () => {
    if (!carId) return;

    if (!formData.brand || !formData.model || !formData.transmission || !formData.carType || !formData.fuelType) {
      setError('Marca, modelo, transmisión, tipo de auto y tipo de combustible son requeridos');
      return;
    }
    setIsLoading(true);
    try {
      await updateCar({
        id: parseInt(carId, 10),
        body: formData,
      }).unwrap();
      navigateToCars();
    } catch (updateError) {
      // eslint-disable-next-line no-console
      console.error('Error updating car:', updateError);
      setError('Error al actualizar el auto');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingCar) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  if (!car) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Auto no encontrado</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={navigateToCars} sx={{ mr: 2 }}>
          Volver
        </Button>
        <Typography variant="h4">
          Editar Auto - {car.brand} {car.model}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Información del Vehículo
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <TextField
            required
            label="Marca"
            value={formData.brand || ''}
            onChange={(e) => handleFieldChange('brand', e.target.value.toUpperCase())}
            fullWidth
            placeholder="Ej: TOYOTA, FORD, etc."
          />

          <TextField
            required
            label="Modelo"
            value={formData.model || ''}
            onChange={(e) => handleFieldChange('model', e.target.value.toUpperCase())}
            fullWidth
            placeholder="Ej: MORNING, RAV4, L200, etc."
          />

          <TextField
            label="Versión"
            value={formData.version || ''}
            onChange={(e) => handleFieldChange('version', e.target.value.toUpperCase())}
            fullWidth
            placeholder="Ej: 1.5 TDI 4x4 highline MT"
          />

          <TextField
            label="Año"
            type="number"
            value={formData.year || ''}
            onChange={(e) => handleFieldChange('year', parseInt(e.target.value, 10))}
            fullWidth
          />

          <TextField
            label="Kilometraje"
            type="number"
            value={formData.km || ''}
            onChange={(e) => handleFieldChange('km', parseInt(e.target.value, 10))}
            fullWidth
          />

          <TextField
            label="Color"
            value={formData.color || ''}
            onChange={(e) => handleFieldChange('color', e.target.value.toUpperCase())}
            fullWidth
          />

          <TextField
            label="Patente"
            value={formData.licensePlate || ''}
            onChange={(e) => handleFieldChange('licensePlate', e.target.value.toUpperCase())}
            fullWidth
          />
          <TextField
            label="Identificador único (Opcional)"
            value={formData.referenceId || ''}
            onChange={(e) => handleFieldChange('referenceId', e.target.value)}
            fullWidth
          />
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Transmisión</InputLabel>
            <Select
              required
              value={formData.transmission || ''}
              onChange={(e) => handleFieldChange('transmission', e.target.value)}
              label="Transmisión"
            >
              <MenuItem value="">Sin especificar</MenuItem>
              {Transmissions.map((transmission) => (
                <MenuItem key={transmission} value={transmission}>
                  {transmission}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Tipo de Auto</InputLabel>
            <Select
              required
              value={formData.carType || ''}
              onChange={(e) => handleFieldChange('carType', e.target.value)}
              label="Tipo de Auto"
            >
              <MenuItem value="">Sin especificar</MenuItem>
              {CarTypes.map((carType) => (
                <MenuItem key={carType} value={carType}>
                  {carType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Tipo de Combustible</InputLabel>
            <Select
              required
              value={formData.fuelType || ''}
              onChange={(e) => handleFieldChange('fuelType', e.target.value)}
              label="Tipo de Combustible"
            >
              <MenuItem value="">Sin especificar</MenuItem>
              {FuelTypes.map((fuelType) => (
                <MenuItem key={fuelType} value={fuelType}>
                  {fuelType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Pricing Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Precios
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <TextField
            label="Precio"
            type="number"
            required
            value={formData.price || ''}
            onChange={(e) => handleFieldChange('price', parseInt(e.target.value, 10))}
            fullWidth
            InputProps={{
              startAdornment: '$',
            }}
          />

          <TextField
            label="Cuota Mensual (Opcional)"
            type="number"
            value={formData.monthlyFee || ''}
            onChange={(e) => handleFieldChange('monthlyFee', parseInt(e.target.value, 10))}
            fullWidth
            InputProps={{
              startAdornment: '$',
            }}
          />
        </Box>
      </Box>

      {/* Additional Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Información Adicional
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <TextField
            label="Sucursal"
            value={formData.branchOfficeDirection || ''}
            onChange={(e) => handleFieldChange('branchOfficeDirection', e.target.value)}
            fullWidth
          />

          <TextField
            label="Link"
            value={formData.link || ''}
            onChange={(e) => handleFieldChange('link', e.target.value)}
            fullWidth
          />

          <TextField
            label="URL de Imagen"
            value={formData.image || ''}
            onChange={(e) => handleFieldChange('image', e.target.value)}
            fullWidth
          />

          <TextField
            label="URL de Imagen Original"
            value={formData.originalImageUrl || ''}
            onChange={(e) => handleFieldChange('originalImageUrl', e.target.value)}
            fullWidth
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField
            label="Promociones"
            value={formData.promotions || ''}
            onChange={(e) => handleFieldChange('promotions', e.target.value)}
            fullWidth
            multiline
            rows={1}
            placeholder="Ej: Precio con financiamiento, precio con descuento, etc."
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
        <Typography variant="body2" fontWeight="bold">
          ⚠️ Importante: Es posible que los autos se actualizan automáticamente desde las fuentes externas de datos. Los
          cambios realizados manualmente pueden ser sobrescritos en futuras actualizaciones automáticas.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={navigateToCars} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => {
            // eslint-disable-next-line no-console
            handleSubmit().catch((submitError) => console.error('Error updating car:', submitError));
          }}
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </Box>
    </Box>
  );
};

export default EditCar;
